import { DOCUMENT } from '@angular/common';
import { Injectable, Signal, computed, effect, inject, linkedSignal } from '@angular/core';

import {
  ANONYMOUS_PREFERENCES_OWNER,
  USER_PREFERENCES_KEY_PREFIX,
  USER_PREFERENCES_STORAGE_KEY,
} from '@common/enums';
import { DEFAULT_USER_PREFERENCES } from '../data/user-preferences.data';
import {
  cloneUserPreferences,
  migrateLegacyPreferences,
  reconcileUserPreferences,
} from '../functions/user-preferences.functions';
import type { UserPreferences } from '../types';

import { AuthService } from './auth.service';

type PreferenceKey = keyof UserPreferences;
type PreferenceValue<K extends PreferenceKey> = UserPreferences[K];
type SectionKey<K extends PreferenceKey> =
  PreferenceValue<K> extends object ? keyof PreferenceValue<K> : never;

/**
 * Remembered per-user state, in localStorage under the signed-in user's Cognito `sub`.
 *
 * The store used to be one shared key, so two people on one browser shared a dashboard and sign-out
 * left the last person's state for the next. `state` is now linked to the session owner: when the
 * owner changes, it re-reads that owner's blob synchronously, so nothing renders a frame of the
 * previous user's preferences.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly document = inject(DOCUMENT);
  private readonly auth = inject(AuthService);

  /** Whose preferences are loaded: the Cognito `sub`, or the signed-out profile. */
  readonly owner = computed<string>(() => this.auth.userData()?.sub ?? ANONYMOUS_PREFERENCES_OWNER);

  private readonly state = linkedSignal<string, UserPreferences>({
    source: this.owner,
    computation: (owner) => this.load(owner),
  });
  private readonly selectors = new Map<PreferenceKey, Signal<unknown>>();

  readonly snapshot = this.state.asReadonly();

  readonly theme = this.select('theme');
  readonly dashboardLayout = this.select('dashboardLayout');
  readonly dateRange = this.select('dateRange');
  readonly reports = this.select('reports');
  readonly tags = this.select('tags');
  readonly scope = this.select('scope');
  readonly organizations = this.select('organizations');

  constructor() {
    effect(() => {
      const owner = this.owner();
      const data = this.state();
      const storage = this.safeStorage();
      if (!storage) return;
      try {
        storage.setItem(storageKeyFor(owner), JSON.stringify(data));
        // The sign-in screen renders as nobody; carrying the theme across stops it flashing the
        // default theme at someone who chose dark.
        if (owner !== ANONYMOUS_PREFERENCES_OWNER) this.carryThemeToSignedOut(storage, data);
      } catch {}
    });
  }

  select<K extends PreferenceKey>(key: K): Signal<PreferenceValue<K>> {
    const existing = this.selectors.get(key) as Signal<PreferenceValue<K>> | undefined;
    if (existing) return existing;
    const selector = computed(() => this.state()[key]);
    this.selectors.set(key, selector);
    return selector;
  }

  get<K extends PreferenceKey>(key: K): PreferenceValue<K> {
    return this.state()[key];
  }

  set<K extends PreferenceKey>(key: K, value: PreferenceValue<K>): void {
    this.state.update((curr) => ({ ...curr, [key]: value }));
  }

  patch<K extends PreferenceKey>(key: K, value: Partial<PreferenceValue<K>>): void {
    this.state.update((curr) => {
      const current = curr[key];
      const next =
        current && typeof current === 'object' && !Array.isArray(current)
          ? ({ ...(current as object), ...(value as object) } as PreferenceValue<K>)
          : (value as PreferenceValue<K>);
      return { ...curr, [key]: next };
    });
  }

  patchIn<K extends PreferenceKey, S extends SectionKey<K>>(
    key: K,
    section: S,
    value: Partial<PreferenceValue<K>[S]>,
  ): void {
    this.state.update((curr) => {
      const container = curr[key] as unknown as Record<string, unknown>;
      const currentSection = (container[section as string] ?? {}) as object;
      return {
        ...curr,
        [key]: {
          ...container,
          [section]: { ...currentSection, ...(value as object) },
        },
      } as UserPreferences;
    });
  }

  reset<K extends PreferenceKey>(key: K): PreferenceValue<K> {
    const value = cloneUserPreferences(DEFAULT_USER_PREFERENCES)[key];
    this.set(key, value);
    return value;
  }

  resetAll(): void {
    this.state.set(cloneUserPreferences(DEFAULT_USER_PREFERENCES));
  }

  static get defaults(): UserPreferences {
    return DEFAULT_USER_PREFERENCES;
  }

  private load(owner: string): UserPreferences {
    const storage = this.safeStorage();
    if (!storage) return cloneUserPreferences(DEFAULT_USER_PREFERENCES);

    const own = this.readJson(storage, storageKeyFor(owner));
    if (own !== null) return reconcileUserPreferences(own);

    if (owner === ANONYMOUS_PREFERENCES_OWNER)
      return cloneUserPreferences(DEFAULT_USER_PREFERENCES);

    // First sign-in since preferences were namespaced: adopt the shared blob, then remove it so the
    // next person to sign in on this browser does not inherit it.
    const shared = this.readJson(storage, USER_PREFERENCES_STORAGE_KEY);
    if (shared !== null) {
      try {
        storage.removeItem(USER_PREFERENCES_STORAGE_KEY);
      } catch {}
      return reconcileUserPreferences(shared);
    }

    return migrateLegacyPreferences(storage);
  }

  private carryThemeToSignedOut(storage: Storage, data: UserPreferences): void {
    const key = storageKeyFor(ANONYMOUS_PREFERENCES_OWNER);
    const current = reconcileUserPreferences(this.readJson(storage, key));
    if (current.theme === data.theme) return;
    storage.setItem(key, JSON.stringify({ ...current, theme: data.theme }));
  }

  private safeStorage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }

  private readJson(storage: Storage, key: string): unknown {
    try {
      const raw = storage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}

function storageKeyFor(owner: string): string {
  return `${USER_PREFERENCES_KEY_PREFIX}:${owner}`;
}
